import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Link, LinkBreak } from '@phosphor-icons/react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const LineItemTypeahead = ({ value, onChange, onSelect, placeholder = 'Type description...', className = '', linkedItem, onUnlink }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [locked, setLocked] = useState(!!linkedItem);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setLocked(!!linkedItem); }, [linkedItem]);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    try {
      const { data } = await axios.get(`${BACKEND}/api/line-item-typeahead?q=${encodeURIComponent(q)}&limit=10`, { withCredentials: true });
      setSuggestions(data);
      setOpen(data.length > 0);
      setHighlighted(-1);
    } catch { setSuggestions([]); }
  }, []);

  const handleChange = (e) => {
    if (locked) return;
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 200);
  };

  const handleSelect = (item) => {
    onChange(item.description);
    setLocked(true);
    setOpen(false);
    setSuggestions([]);
    if (onSelect) {
      onSelect({
        ...item,
        master_item_id: item.item_code ? (item.id || '') : '',
        item_code: item.item_code || '',
        source_type: item.source === 'history' ? 'quote_history' : 'master',
      });
    }
  };

  const handleUnlock = () => {
    setLocked(false);
    if (onUnlink) onUnlink();
  };

  const handleKeyDown = (e) => {
    if (locked) return;
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && highlighted >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative" data-testid="line-item-typeahead">
      {locked ? (
        <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-300 rounded-none p-1.5 text-xs" data-testid="typeahead-locked">
          <Link size={13} className="text-blue-600 shrink-0" />
          <span className="flex-1 text-blue-800 font-medium truncate">{value}</span>
          {linkedItem?.item_code && <span className="text-[9px] font-mono text-blue-500 shrink-0">{linkedItem.item_code}</span>}
          <button type="button" onClick={handleUnlock} className="text-blue-400 hover:text-red-500 shrink-0 p-0.5" data-testid="typeahead-unlock" title="Unlink from master">
            <X size={13} />
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
            placeholder={placeholder}
            className={`w-full bg-zinc-50 border border-zinc-200 rounded-none p-1.5 text-xs text-zinc-800 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none ${className}`}
            data-testid="typeahead-input"
            autoComplete="off"
          />
          {linkedItem === null && value && (
            <div className="flex items-center gap-1 mt-0.5" data-testid="master-link-removed">
              <LinkBreak size={11} className="text-amber-500" />
              <span className="text-[9px] text-amber-600 font-medium">Master link removed</span>
            </div>
          )}
        </>
      )}
      {open && suggestions.length > 0 && !locked && (
        <div className="absolute z-50 top-full left-0 w-full mt-0.5 bg-white border border-zinc-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto" data-testid="typeahead-dropdown">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(s)}
              className={`w-full text-left px-3 py-2 text-xs border-b border-zinc-50 hover:bg-blue-50 transition-colors ${
                i === highlighted ? 'bg-blue-50' : ''
              }`}
              data-testid={`typeahead-option-${i}`}
            >
              <div className="flex items-center gap-1.5">
                {s.item_code && <span className="font-mono text-[9px] text-blue-600 bg-blue-50 px-1 py-0.5 rounded shrink-0">{s.item_code}</span>}
                <span className="font-medium text-zinc-800">{s.description}</span>
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5">
                {[s.unit, s.trade_name, s.specification].filter(Boolean).join(' | ')}
                {s.usage_count > 1 && <span className="ml-1 text-zinc-300">used {s.usage_count}x</span>}
                {s.source === 'history' && <span className="ml-1 text-zinc-300 italic">from quotes</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
